import React, { useState } from 'react';
import UnitCard from './UnitCard';
import UnitEditor from './UnitEditor';
import './AccommodationUnits.css';

const AccommodationUnits = ({ formData, updateField, canEdit = true }) => {
    const [showEditor, setShowEditor] = useState(false);

    const [editingIndex, setEditingIndex] = useState(null);

    const units = formData?.units || [];

    // =========================================================
    // ADD UNIT
    // =========================================================

    const handleAddUnit = () => {
        setEditingIndex(null);
        setShowEditor(true);
    };

    // =========================================================
    // EDIT UNIT
    // =========================================================

    const handleEditUnit = (unit, index) => {
        setEditingIndex(index);
        setShowEditor(true);
    };

    // =========================================================
    // DELETE UNIT
    // =========================================================

    const handleDeleteUnit = (unit, index) => {
        const confirmed = window.confirm(`Are you sure you want to delete "${unit?.name || 'this unit'}"?`);

        if (!confirmed) return;

        const updatedUnits = units.filter((_, unitIndex) => unitIndex !== index);

        updateField('units', updatedUnits);
    };

    // =========================================================
    // DUPLICATE UNIT
    // =========================================================

    const handleDuplicateUnit = (unit) => {
        // the unit is in the form's shape (utils/accommodationTransforms/units.js
        // reverseUnit / UnitEditor): copy it deeply and drop every saved id
        const copy = typeof structuredClone === 'function' ? structuredClone(unit) : JSON.parse(JSON.stringify(unit));
        const withoutId = (item) => (item && typeof item === 'object' ? { ...item, _id: undefined } : item);

        const duplicatedUnit = {
            ...copy,

            // New unit identity
            _id: undefined,
            id: undefined,

            name: `${unit.name || 'Unit'} Copy`,

            shortName: unit.shortName ? `${unit.shortName} Copy` : '',

            displayOrder: units.length,

            beds: (copy.beds || []).map(withoutId),

            images: (copy.images || []).map(withoutId),

            pricingPlans: (copy.pricingPlans || []).map(withoutId),

            pricingRules: (copy.pricingRules || []).map(withoutId),

            services: (copy.services || []).map(withoutId)
        };

        updateField('units', [...units, duplicatedUnit]);
    };

    // =========================================================
    // SAVE UNIT
    // =========================================================

    const handleSaveUnit = (unitData, index) => {
        if (index !== null && index !== undefined) {
            const updatedUnits = units.map((existingUnit, unitIndex) =>
                unitIndex === index ? unitData : existingUnit
            );

            updateField('units', updatedUnits);
        } else {
            const newUnit = {
                ...unitData,

                displayOrder: units.length
            };

            updateField('units', [...units, newUnit]);
        }

        setShowEditor(false);
        setEditingIndex(null);
    };

    // =========================================================
    // CANCEL EDITOR
    // =========================================================

    const handleCancelEditor = () => {
        setShowEditor(false);
        setEditingIndex(null);
    };

    // =========================================================
    // CURRENT UNIT
    // =========================================================

    const editingUnit = editingIndex !== null ? units[editingIndex] : null;

    // =========================================================
    // EDITOR
    // =========================================================

    if (showEditor) {
        return (
            <div className='accommodation-units'>
                <UnitEditor
                    unit={editingUnit}
                    index={editingIndex}
                    onSave={handleSaveUnit}
                    onCancel={handleCancelEditor}
                    canEdit={canEdit}
                />
            </div>
        );
    }

    // =========================================================
    // UNIT LIST
    // =========================================================

    return (
        <div className='accommodation-units'>
            {/* =================================================
                HEADER
            ================================================= */}

            <div className='units-page-header'>
                <div>
                    <span className='units-page-label'>Accommodation Inventory</span>

                    <h2>Rooms & Units</h2>

                    <p>Add the rooms, villas, tents, cottages or other bookable units available at this property.</p>
                </div>

                {canEdit && (
                    <button type='button' className='add-unit-button' onClick={handleAddUnit}>
                        <span>+</span>
                        Add Unit
                    </button>
                )}
            </div>

            {/* =================================================
                SUMMARY
            ================================================= */}

            <div className='units-summary'>
                <div className='unit-summary-card'>
                    <span>Accommodation Types</span>

                    <strong>{units.length}</strong>
                </div>

                <div className='unit-summary-card'>
                    <span>Total Inventory</span>

                    <strong>
                        {units.reduce(
                            (total, unit) => total + Number(unit?.inventory?.totalUnits || unit?.totalUnits || 0),
                            0
                        )}
                    </strong>
                </div>

                <div className='unit-summary-card'>
                    <span>Active Inventory</span>

                    <strong>
                        {units.reduce((total, unit) => total + Number(unit?.inventory?.activeUnits || 0), 0)}
                    </strong>
                </div>
            </div>

            {/* =================================================
                EMPTY STATE
            ================================================= */}

            {units.length === 0 ? (
                <div className='units-empty-state'>
                    <div className='empty-unit-icon'>🏕️</div>

                    <h3>No accommodation units yet</h3>

                    <p>Add your first room, villa, tent, campsite or other bookable unit.</p>

                    {canEdit && (
                        <button type='button' className='add-unit-button empty-button' onClick={handleAddUnit}>
                            <span>+</span>
                            Add Your First Unit
                        </button>
                    )}
                </div>
            ) : (
                /* =================================================
                   UNIT LIST
                ================================================= */

                <div className='units-list'>
                    {units.map((unit, index) => (
                        <UnitCard
                            key={unit._id || unit.id || `unit-${index}`}
                            unit={unit}
                            index={index}
                            onEdit={handleEditUnit}
                            onDelete={handleDeleteUnit}
                            onDuplicate={handleDuplicateUnit}
                            canEdit={canEdit}
                        />
                    ))}
                </div>
            )}

            {/* =================================================
                BOTTOM ADD BUTTON
            ================================================= */}

            {units.length > 0 && canEdit && (
                <button type='button' className='add-another-unit' onClick={handleAddUnit}>
                    + Add Another Unit
                </button>
            )}
        </div>
    );
};

export default AccommodationUnits;
